import './FacetGroupContent.scss';
import React, {useMemo, useState} from 'react';
import {FacetListItem} from './FacetListItem/FacetListItem';
import {FacetValue, HibuEqualsFilter, HibuFacetErrorCode, HibuFilter} from '../../../facet.types';
import {HibuTextButton} from '../../../../common/hibuTextButton/HibuTextButton';
import {useTranslation} from 'react-i18next';
import {useLocalizationUtil} from '../../../../localizationUtil';
import {useIsEqualsFilterSelectedFunction} from '../../../duck/filter.hooks';
import {FacetErrorMessage} from '../../../FacetErrorMessage';

type FacetGroupMultiSelectProps = {
    isHistogram: boolean;
    isExpanded: boolean;
    sortedValues: FacetValue[];
    fieldName: string;
    errorCode: HibuFacetErrorCode | undefined;
    selectedFilters: HibuFilter[];
};

const DEFAULT_VISIBLE_COUNT = 8;

const getSelectedValuesBeyondCount = (allValues: FacetValue[], selectedValues: FacetValue[]): FacetValue[] => {
    return selectedValues.filter(val => !allValues.includes(val));
};

export const FacetGroupMultiSelect = React.memo(({
                                                     isHistogram,
                                                     isExpanded,
                                                     sortedValues,
                                                     fieldName,
                                                     errorCode,
                                                     selectedFilters,
                                                 }: FacetGroupMultiSelectProps) => {
    const {t} = useTranslation();
    const {formatNumber} = useLocalizationUtil();
    const [showAll, setShowAll] = useState<boolean>(false);

    const isEqualsFilterSelected: (filter: HibuEqualsFilter, selectedFilters: HibuFilter[]) => boolean = useIsEqualsFilterSelectedFunction();
    const selectedValues = useMemo(() => {
        return sortedValues.filter(value =>
            isEqualsFilterSelected(value.filter as HibuEqualsFilter, selectedFilters)
        );
    }, [sortedValues, isEqualsFilterSelected, selectedFilters]);

    const isShowMoreNeeded: boolean = sortedValues.length > DEFAULT_VISIBLE_COUNT;

    /**
     *  This function returns the facet list items to be shown
     *  If group is collapsed: only the selected values will be shown
     *  If group is expanded and showAll is selected: All values will be shown
     *  Otherwise: Top 8 values + selected values beyond DEFAULT_VISIBLE_COUNT will be shown
     */
    const valuesToBeShownEnhanced: FacetValue[] = useMemo<FacetValue[]>((): FacetValue[] => {
        if (!isExpanded) return selectedValues;

        if (showAll || sortedValues.length <= DEFAULT_VISIBLE_COUNT) return sortedValues;

        const head = sortedValues.slice(0, DEFAULT_VISIBLE_COUNT);
        const tail = getSelectedValuesBeyondCount(head, selectedValues);

        return [...head, ...tail];
    }, [isExpanded, showAll, sortedValues, selectedValues]);

    const showMoreLabel = useMemo<string>((): string => {
        const showMoreCount: number = sortedValues.length - (valuesToBeShownEnhanced.length || DEFAULT_VISIBLE_COUNT);
        return showAll
            ? t('general.hideAll')
            : t('general.seeAll', {entry: formatNumber(showMoreCount)});
    }, [formatNumber, showAll, sortedValues.length, t, valuesToBeShownEnhanced]);

    if (isHistogram) return null;

    return (
        <>
            {valuesToBeShownEnhanced.map(((value: FacetValue) =>
                    <FacetListItem value={value} fieldName={fieldName} key={value.name}
                                   selectedFilters={selectedFilters}
                                   showCount={errorCode !== HibuFacetErrorCode.COUNT_NOT_AVAILABLE}/>
            ))}
            {isExpanded && isShowMoreNeeded &&
                <HibuTextButton className="show-more-facet-values" label={showMoreLabel}
                                onClick={() => setShowAll(!showAll)}
                                disabled={false}>
                </HibuTextButton>
            }
            {isExpanded &&
                <FacetErrorMessage errorCode={errorCode}/>
            }
        </>

    );
});
